import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { GitBranch, ChevronUp, ChevronDown, Trash2, Plus, Save, Lock, Flag, ShieldCheck, ClipboardCheck } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SectionTitle } from './common';

// C13 / Part 4 — Configurable Approval Chain editor (Settings). Reused for two
// distinct workflows via the `endpoint` prop:
//   • /approval-chain      — budget revisions (full chain) + the two tender hops.
//   • /cc-opening-workflow — Cost Centre Opening approval chain (Part 4).
// Both are Admin-only and audited; in-flight items stay locked to the chain in
// force when they were raised/submitted.
export const ApprovalChainEditor = ({
  endpoint = '/approval-chain',
  title = 'Approval Chain',
  description = 'The ordered approval stages. Drives budget revisions end-to-end, and the two tender approval hops.',
  showTenderHops = true,
  icon: Icon = GitBranch,
  testid = 'approval-chain-editor',
  savedMessage = 'Approval chain updated',
} = {}) => {
  const [stages, setStages] = useState(null);
  const [hops, setHops] = useState({ manager: '—', admin: '—' });
  const [canEdit, setCanEdit] = useState(false);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint);
      setStages(data.stages || []);
      setHops(data.tenderHops || { manager: '—', admin: '—' });
      setCanEdit(!!data.canEdit);
      if (data.canEdit) {
        try { const g = await api.get('/groups'); setGroups((g.data || []).map((x) => x.name)); } catch (e) { console.warn('Failed to load groups', e); }
      }
    } catch { setStages([]); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  if (stages === null) return null;

  // Live preview of derived tender hops as the admin edits.
  const managerHop = stages.length > 1 ? stages[1].groupName : '—';
  const adminHop = stages.length > 1 ? stages[stages.length - 1].groupName : '—';

  const update = (i, key, val) => setStages((s) => s.map((st, idx) => (idx === i ? { ...st, [key]: val } : st)));
  const move = (i, dir) => setStages((s) => {
    const j = i + dir;
    if (j < 0 || j >= s.length) return s;
    const next = [...s];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const remove = (i) => setStages((s) => (s.length <= 2 ? s : s.filter((_, idx) => idx !== i)));
  const addStage = () => setStages((s) => [...s, { key: `stage_${Date.now()}`, label: 'New approval stage', groupName: groups[0] || '', role: 'approver' }]);

  const save = async () => {
    if (stages.some((s) => !s.label?.trim() || !s.groupName?.trim())) { toast.error('Every stage needs a label and a group.'); return; }
    setSaving(true);
    try {
      const { data } = await api.put(endpoint, { stages: stages.map((s, i) => ({ ...s, role: i === 0 ? 'initiator' : 'approver' })) });
      setStages(data.stages); if (data.tenderHops) setHops(data.tenderHops);
      toast.success(savedMessage);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the chain');
    } finally { setSaving(false); }
  };

  return (
    <div className="erak-card space-y-4 p-5" data-testid={testid}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <SectionTitle><Icon size={15} className="mr-1.5 inline" />{title}</SectionTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {description}
            {' '}<span className="inline-flex items-center gap-1"><Lock size={11} />In-flight approvals stay locked to the chain in force when they were raised.</span>
          </p>
        </div>
        {canEdit && (
          <Button size="sm" data-testid={`${testid}-save`} onClick={save} disabled={saving}>
            <Save size={14} className="mr-1.5" />{saving ? 'Saving…' : 'Save chain'}
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {stages.map((st, i) => (
          <div key={st.key || i} data-testid={`${testid}-stage-${i}`}
            className="flex flex-wrap items-center gap-2 rounded-xl border bg-card/50 p-2.5">
            <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[hsl(var(--primary)/0.12)] text-xs font-bold font-num">{i + 1}</div>
            {i === 0
              ? <Badge variant="secondary" className="gap-1 text-[10px] text-[hsl(var(--teal))]"><Flag size={10} />Initiator</Badge>
              : <Badge variant="outline" className="text-[10px]">Approver</Badge>}
            <Input className="h-8 w-44" data-testid={`${testid}-stage-label-${i}`} value={st.label || ''} disabled={!canEdit}
              onChange={(e) => update(i, 'label', e.target.value)} placeholder="Stage label" />
            {canEdit && groups.length > 0 ? (
              <Select value={st.groupName || ''} onValueChange={(v) => update(i, 'groupName', v)}>
                <SelectTrigger className="h-8 w-52" data-testid={`${testid}-stage-group-${i}`}><SelectValue placeholder="Select group" /></SelectTrigger>
                <SelectContent>
                  {groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  {!groups.includes(st.groupName) && st.groupName && <SelectItem value={st.groupName}>{st.groupName}</SelectItem>}
                </SelectContent>
              </Select>
            ) : (
              <span className="rounded-md border px-2.5 py-1 text-xs font-medium" data-testid={`${testid}-stage-group-readonly-${i}`}>{st.groupName}</span>
            )}
            {canEdit && (
              <div className="ml-auto flex items-center gap-1">
                <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`${testid}-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)}><ChevronUp size={14} /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`${testid}-down-${i}`} disabled={i === stages.length - 1} onClick={() => move(i, 1)}><ChevronDown size={14} /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[hsl(var(--danger))]" data-testid={`${testid}-remove-${i}`} disabled={stages.length <= 2} onClick={() => remove(i)}><Trash2 size={14} /></Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {canEdit && (
        <Button size="sm" variant="outline" data-testid={`${testid}-add`} onClick={addStage} disabled={!groups.length}>
          <Plus size={14} className="mr-1.5" />Add stage
        </Button>
      )}

      {showTenderHops && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-muted/40 p-3 text-xs" data-testid="tender-hops-preview">
          <ShieldCheck size={14} className="text-[hsl(var(--copper))]" />
          <span className="font-medium">Tender approval hops:</span>
          <Badge variant="secondary">Manager → {canEdit ? managerHop : hops.manager}</Badge>
          <Badge variant="secondary">Admin → {canEdit ? adminHop : hops.admin}</Badge>
          <span className="text-muted-foreground">(first approver group handles management review; the final group gives final approval)</span>
        </div>
      )}

      {!showTenderHops && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-muted/40 p-3 text-xs" data-testid={`${testid}-note`}>
          <ClipboardCheck size={14} className="text-[hsl(var(--brand))]" />
          <span className="text-muted-foreground">A completed Cost Centre Opening is routed through these stages in order. Only a fully approved opening can open the cost centre.</span>
        </div>
      )}
    </div>
  );
};
